import { supabase } from '../utils/supabase';
import cacheService from './cacheService';

const isDev = import.meta.env?.MODE === 'development' || import.meta.env?.DEV;

/**
 * Fetches dashboard metrics for a user (with Redis caching)
 * @param {string} userId - The user ID to fetch metrics for
 * @param {boolean} forceRefresh - Force fresh data from database
 * @returns {Promise<Object>} - Object containing dashboard metrics
 */
export const getUserDashboardMetrics = async (userId, forceRefresh = false) => {
  if (!userId) {
    throw new Error('User ID is required to fetch dashboard metrics');
  }

  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cacheKey = `va_dashboard:${userId}`;
    const cached = await cacheService.get(cacheKey);
    
    if (cached) {
      if (isDev) {
        console.log(`[VA Dashboard] Cache hit for user ${userId}`);
      }
      return cached;
    }
    
    if (isDev) {
      console.log(`[VA Dashboard] Cache miss for user ${userId}`);
    }
  }

  try {
    // Get active applications count and pending responses
    const { data: applications, error: applicationsError } = await supabase
      .from('job_applications')
      .select('id, status')
      .eq('applicant_id', userId);
      
    if (applicationsError) throw applicationsError;
    
    const activeApplications = applications.length;
    const pendingResponses = applications.filter(app => app.status === 'pending').length;
    
    // Get profile views in the last 7 days (with error handling for missing table)
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    let profileViews = [];
    let prevProfileViews = [];
    
    try {
      const { data: profileViewsData, error: profileViewsError } = await supabase
        .from('profile_views')
        .select('id')
        .eq('profile_id', userId)
        .gte('viewed_at', oneWeekAgo.toISOString());
        
      if (profileViewsError) {
        if (profileViewsError.code === '42P01') {
          // Table doesn't exist yet, use empty array
          console.log('Profile views table not found, using default values');
        } else {
          throw profileViewsError;
        }
      } else {
        profileViews = profileViewsData || [];
      }
      
      // Get previous week's profile views for comparison
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      
      const { data: prevProfileViewsData, error: prevProfileViewsError } = await supabase
        .from('profile_views')
        .select('id')
        .eq('profile_id', userId)
        .gte('viewed_at', twoWeeksAgo.toISOString())
        .lt('viewed_at', oneWeekAgo.toISOString());
        
      if (prevProfileViewsError) {
        if (prevProfileViewsError.code !== '42P01') {
          throw prevProfileViewsError;
        }
      } else {
        prevProfileViews = prevProfileViewsData || [];
      }
    } catch (error) {
      if (error.code === '42P01') {
        console.log('Profile views table not found, using default values');
      } else {
        console.warn('Error fetching profile views:', error);
      }
    }
    
    // Calculate profile views trend
    const currentViews = profileViews.length;
    const previousViews = prevProfileViews.length;
    let profileViewsTrend = 'neutral';
    let profileViewsTrendValue = '0%';
    
    if (previousViews > 0) {
      const percentChange = Math.round(((currentViews - previousViews) / previousViews) * 100);
      profileViewsTrend = percentChange > 0 ? 'up' : percentChange < 0 ? 'down' : 'neutral';
      profileViewsTrendValue = `${percentChange > 0 ? '+' : ''}${percentChange}%`;
    } else if (currentViews > 0) {
      profileViewsTrend = 'up';
      profileViewsTrendValue = '+100%';
    }
    
    // Get match score (average match score from available jobs) with error handling
    let jobMatches = [];
    let matchScore = 0;
    let matchScoreTrend = 'neutral';
    let matchScoreTrendValue = '0%';
    
    try {
      // First, get available published jobs to calculate fresh matches
      const { data: availableJobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id, title, skills_required, min_experience_years, budget_min, budget_max, location, is_remote')
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .limit(20); // Check against recent jobs

      if (jobsError) throw jobsError;

      if (availableJobs && availableJobs.length > 0) {
        // Get current user's profile for matching
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select(`
            id,
            skills,
            experience_years,
            hourly_rate,
            location,
            user_analytics (
              average_rating,
              total_earnings
            )
          `)
          .eq('id', userId)
          .single();

        if (!profileError && userProfile) {
          // Import the matching service dynamically to avoid circular dependencies
          const { MatchingScoreService } = await import('./matchingScoreService.js');
          
          const matchPromises = availableJobs.map(async (job) => {
            try {
              const matchResult = MatchingScoreService.calculateMatchScore(userProfile, job);
              return {
                job_id: job.id,
                match_score: matchResult.overallScore,
                job_title: job.title
              };
            } catch (error) {
              console.warn(`Error calculating match for job ${job.id}:`, error);
              return null;
            }
          });

          const matchResults = await Promise.all(matchPromises);
          const validMatches = matchResults.filter(match => match !== null);

          if (validMatches.length > 0) {
            // Calculate average match score from current available jobs
            const totalScore = validMatches.reduce((sum, match) => sum + match.match_score, 0);
            matchScore = Math.round(totalScore / validMatches.length);
            
            // Set trend based on match quality
            if (matchScore > 75) {
              matchScoreTrend = 'up';
              matchScoreTrendValue = '+8%';
            } else if (matchScore < 50) {
              matchScoreTrend = 'down';
              matchScoreTrendValue = '-5%';
            } else {
              matchScoreTrend = 'neutral';
              matchScoreTrendValue = '+2%';
            }
          }
        }
      }

      // Fallback: try to get existing job matches if dynamic calculation fails
      if (matchScore === 0) {
        const { data: jobMatchesData, error: jobMatchesError } = await supabase
          .from('job_matches')
          .select('match_score')
          .eq('candidate_id', userId)
          .order('created_at', { ascending: false })
          .limit(10);
          
        if (!jobMatchesError && jobMatchesData && jobMatchesData.length > 0) {
          jobMatches = jobMatchesData;
          const totalScore = jobMatches.reduce((sum, match) => sum + match.match_score, 0);
          matchScore = Math.round(totalScore / jobMatches.length);
          
          if (matchScore > 75) {
            matchScoreTrend = 'up';
            matchScoreTrendValue = '+5%';
          } else if (matchScore < 50) {
            matchScoreTrend = 'down';
            matchScoreTrendValue = '-2%';
          }
        }
      }
        
    } catch (error) {
      console.warn('Error calculating match scores:', error);
      // Set default values if calculation fails
      matchScore = 0;
    }
    
    // Get response rate (for messages in the last 30 days) with improved calculation
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    let responseRate = 0;
    let responseRateTrend = 'neutral';
    let responseRateTrendValue = '0%';
    
    try {
      // First, try the existing response tracking method
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select('id, response_time_minutes, is_responded, created_at')
        .eq('recipient_id', userId)
        .gte('created_at', thirtyDaysAgo.toISOString());
      
      let hasValidResponseData = false;
      
      if (!messagesError && messagesData && messagesData.length > 0) {
        // Check if the is_responded tracking system has actually been used.
        // NOTE: is_responded has a DB default of false (not null), so checking
        // `!== null` incorrectly returns true for every row. We must check for
        // at least one message explicitly marked as responded (=== true) or with
        // a recorded response time.
        hasValidResponseData = messagesData.some(msg =>
          msg.is_responded === true || msg.response_time_minutes !== null
        );

        if (hasValidResponseData) {
          const respondedMessages = messagesData.filter(msg => 
            msg.is_responded === true || (msg.response_time_minutes !== null && msg.response_time_minutes <= 1440)
          );
          responseRate = Math.round((respondedMessages.length / messagesData.length) * 100);
        }
      }
      
      // If response tracking data is not available or unreliable, use conversation-based calculation
      if (!hasValidResponseData) {
        // Get ALL conversations where the user is a participant (no creation-date filter —
        // VAs can reply to conversations started more than 30 days ago).
        const { data: conversations, error: convError } = await supabase
          .from('conversations')
          .select('id, participant_1, participant_2')
          .or(`participant_1.eq.${userId},participant_2.eq.${userId}`);
          
        if (!convError && conversations && conversations.length > 0) {
          let conversationsWithResponse = 0;
          let conversationsWithIncomingMessages = 0;

          // For each conversation, check if there were incoming messages in the last 30 days
          // and whether the VA replied to them in that same window.
          for (const conv of conversations) {
            const otherId = conv.participant_1 === userId ? conv.participant_2 : conv.participant_1;

            // Check if the other person sent a message in the last 30 days
            const { data: incomingMsgs, error: incomingMsgError } = await supabase
              .from('messages')
              .select('id')
              .eq('conversation_id', conv.id)
              .eq('sender_id', otherId)
              .gte('sent_at', thirtyDaysAgo.toISOString())
              .limit(1);

            if (incomingMsgError) {
              console.error('Error fetching incoming messages for conversation', conv.id, incomingMsgError);
              continue;
            }

            if (!incomingMsgs || incomingMsgs.length === 0) continue; // No recent activity
            conversationsWithIncomingMessages++;

            // Check if the VA sent at least one reply in the last 30 days
            const { data: userMessages, error: userMsgError } = await supabase
              .from('messages')
              .select('id')
              .eq('conversation_id', conv.id)
              .eq('sender_id', userId)
              .gte('sent_at', thirtyDaysAgo.toISOString())
              .limit(1);
              
            if (!userMsgError && userMessages && userMessages.length > 0) {
              conversationsWithResponse++;
            }
          }
          
          if (conversationsWithIncomingMessages > 0) {
            responseRate = Math.round((conversationsWithResponse / conversationsWithIncomingMessages) * 100);
          } else if (conversations.length > 0) {
            // VA is in conversations but no one messaged them recently — treat as N/A, default 100
            responseRate = 100;
          }
        } else {
          // Fallback: Check message activity ratio
          const { data: receivedMessages, error: receivedError } = await supabase
            .from('messages')
            .select('id')
            .eq('recipient_id', userId)
            .gte('created_at', thirtyDaysAgo.toISOString());
            
          const { data: sentMessages, error: sentError } = await supabase
            .from('messages')
            .select('id')
            .eq('sender_id', userId)
            .gte('created_at', thirtyDaysAgo.toISOString());
            
          if (!receivedError && !sentError && receivedMessages && sentMessages) {
            const receivedCount = receivedMessages.length;
            const sentCount = sentMessages.length;
            
            if (receivedCount > 0) {
              // Calculate response rate based on message activity ratio
              // If someone sends messages, they're likely responding
              responseRate = Math.min(100, Math.round((sentCount / receivedCount) * 100));
            } else if (sentCount > 0) {
              // If they're sending messages without receiving any, set a moderate rate
              responseRate = 75;
            }
          }
        }
      }
      
      // Calculate trend based on previous period
      try {
        const sixtyDaysAgo = new Date();
        sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
        
        // Get previous period's messages for trend calculation
        const { data: prevReceived, error: prevReceivedError } = await supabase
          .from('messages')
          .select('id')
          .eq('recipient_id', userId)
          .gte('created_at', sixtyDaysAgo.toISOString())
          .lt('created_at', thirtyDaysAgo.toISOString());
          
        const { data: prevSent, error: prevSentError } = await supabase
          .from('messages')
          .select('id')
          .eq('sender_id', userId)
          .gte('created_at', sixtyDaysAgo.toISOString())
          .lt('created_at', thirtyDaysAgo.toISOString());
          
        if (!prevReceivedError && !prevSentError && prevReceived && prevSent) {
          const prevReceivedCount = prevReceived.length;
          const prevSentCount = prevSent.length;
          let prevResponseRate = 0;
          
          if (prevReceivedCount > 0) {
            prevResponseRate = Math.min(100, Math.round((prevSentCount / prevReceivedCount) * 100));
          }
          
          const rateDiff = responseRate - prevResponseRate;
          responseRateTrend = rateDiff > 5 ? 'up' : rateDiff < -5 ? 'down' : 'neutral';
          responseRateTrendValue = `${rateDiff > 0 ? '+' : ''}${rateDiff}%`;
        }
      } catch (error) {
        console.warn('Error calculating response rate trend:', error);
      }
        
    } catch (error) {
      console.warn('Error calculating response rate:', error);
      // Set default values if calculation fails
      responseRate = 0;
    }
    
    // Return compiled metrics
    const metrics = [
      {
        title: "Active Applications",
        value: activeApplications.toString(),
        subtitle: `${pendingResponses} pending responses`,
        icon: "Send",
        trend: "up", // We could calculate this but for now assuming it's up
        trendValue: "+2", // Would need historical data to calculate properly
        color: "primary"
      },
      {
        title: "Profile Views",
        value: currentViews.toString(),
        subtitle: "This week",
        icon: "Eye",
        trend: profileViewsTrend,
        trendValue: profileViewsTrendValue,
        color: "success",
        metricType: "profile_views"
      },
      {
        title: "Match Score",
        value: `${matchScore}%`,
        subtitle: matchScore > 80 ? "Above average" : matchScore > 60 ? "Average" : "Below average",
        icon: "Target",
        trend: matchScoreTrend,
        trendValue: matchScoreTrendValue,
        color: "accent",
        metricType: "match_score"
      },
      {
        title: "Response Rate",
        value: `${responseRate}%`,
        subtitle: "Last 30 days",
        icon: "MessageCircle",
        trend: responseRateTrend,
        trendValue: responseRateTrendValue,
        color: responseRate > 80 ? "success" : responseRate > 60 ? "accent" : "warning",
        metricType: "response_rate"
      }
    ];
    
    // Cache the results (3 minutes TTL)
    const cacheKey = `va_dashboard:${userId}`;
    await cacheService.set(cacheKey, metrics, 180);
    
    if (isDev) {
      console.log(`[VA Dashboard] Cached metrics for user ${userId}`);
    }
    
    return metrics;
  } catch (error) {
    console.error('Error fetching dashboard metrics:', error);
    // Return default metrics in case of error
    return [
      {
        title: "Active Applications",
        value: "0",
        subtitle: "0 pending responses",
        icon: "Send",
        trend: "neutral",
        trendValue: "0",
        color: "primary"
      },
      {
        title: "Profile Views",
        value: "0",
        subtitle: "This week",
        icon: "Eye",
        trend: "neutral",
        trendValue: "0%",
        color: "success",
        metricType: "profile_views"
      },
      {
        title: "Match Score",
        value: "0%",
        subtitle: "Not enough data",
        icon: "Target",
        trend: "neutral",
        trendValue: "0%",
        color: "accent"
      },
      {
        title: "Response Rate",
        value: "0%",
        subtitle: "Last 30 days",
        icon: "MessageCircle",
        trend: "neutral",
        trendValue: "0%",
        color: "warning"
      }
    ];
  }
};
